import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { LABEL_TEMPLATES } from '../../utils/printUtils';
import { getTrackingUrl } from '../../utils/trackingNumberUtils';

/**
 * Shipping Label Full Component
 * สำหรับพิมพ์หลังมี tracking number (แสดงข้อมูลครบถ้วน)
 */
const ShippingLabelFull = ({ 
  order, 
  store, 
  templateType = 'a4',
  editedData = null 
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [trackingQrCodeUrl, setTrackingQrCodeUrl] = useState(null);
  
  // ใช้ข้อมูลที่แก้ไขแล้ว หรือข้อมูลเดิม
  const displayData = editedData || {
    buyerName: order?.buyerName || '',
    buyerPhone: order?.buyerPhone || '',
    shippingAddress: order?.shippingAddress || '',
    notes: order?.notes || ''
  };

  // Generate QR Code สำหรับเบอร์โทรผู้รับ
  useEffect(() => {
    const generateQRCodes = async () => {
      // QR Code เบอร์โทร
      if (displayData.buyerPhone) {
        try {
          const qrData = `tel:${displayData.buyerPhone}`;
          const qrCodeDataURL = await QRCode.toDataURL(qrData, {
            width: 200,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
          setQrCodeUrl(qrCodeDataURL);
        } catch (error) {
          console.error('Error generating phone QR code:', error);
        }
      }

      // QR Code Tracking URL (ถ้ามี)
      if (order?.trackingNumber && order?.courierCompany) {
        try {
          const trackingUrl = getTrackingUrl(order.trackingNumber, order.courierCompany);
          if (trackingUrl) {
            const trackingQrCodeDataURL = await QRCode.toDataURL(trackingUrl, {
              width: 200,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            setTrackingQrCodeUrl(trackingQrCodeDataURL);
          }
        } catch (error) {
          console.error('Error generating tracking QR code:', error);
        }
      }
    };

    generateQRCodes();
  }, [displayData.buyerPhone, order?.trackingNumber, order?.courierCompany]);

  const template = LABEL_TEMPLATES[templateType.toUpperCase()] || LABEL_TEMPLATES.A4;
  const courierCompany = order?.customCourierCompany || order?.courierCompany || '';

  return (
    <div 
      id="shipping-label-full-print"
      className="print-content"
      style={{
        width: template.width,
        minHeight: template.height,
        margin: '0 auto',
        padding: '1.2cm',
        fontFamily: "'Sarabun', 'Kanit', Arial, sans-serif",
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        color: '#000000',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        colorAdjust: 'exact'
      }}
    >
      {/* Header - Store Info */}
      <div className="mb-5 pb-3 border-b-2 border-gray-800" style={{ borderBottomWidth: '2px', marginBottom: '16px', paddingBottom: '12px' }}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {store?.logo && (
              <div className="mb-2">
                <img 
                  src={store.logo} 
                  alt={store.name}
                  style={{ maxHeight: '45px', maxWidth: '140px', objectFit: 'contain' }}
                />
              </div>
            )}
            <h2 className="text-lg font-bold text-gray-800 mb-1" style={{ fontSize: '18px', lineHeight: '1.4', marginBottom: '4px' }}>
              {store?.name || 'ร้านค้า'}
            </h2>
            {store?.contact?.address && (
              <p className="text-xs text-gray-600 mb-1" style={{ fontSize: '11px', marginBottom: '4px', lineHeight: '1.4' }}>
                {store.contact.address}
              </p>
            )}
            <div className="flex gap-3 mt-1 text-xs text-gray-600" style={{ fontSize: '11px', marginTop: '4px' }}>
              {store?.contact?.phone && <span>📞 {store.contact.phone}</span>}
              {store?.contact?.email && <span>📧 {store.contact.email}</span>}
            </div>
          </div>
          <div className="text-right ml-4" style={{ marginLeft: '16px' }}>
            <div className="text-sm font-semibold text-gray-700 mb-1" style={{ fontSize: '13px', marginBottom: '4px' }}>
              ใบส่งของ
            </div>
            <div className="text-xs text-gray-600" style={{ fontSize: '11px' }}>
              {new Date().toLocaleDateString('th-TH')}
            </div>
          </div>
        </div>
      </div>

      {/* Order Info Section */}
      <div className="mb-5 grid grid-cols-2 gap-4" style={{ marginBottom: '16px' }}>
        <div 
          className="p-3 bg-gray-50 rounded border border-gray-200" 
          style={{ 
            padding: '10px',
            backgroundColor: '#f9fafb',
            borderColor: '#e5e7eb',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '0.5rem'
          }}
        >
          <div 
            className="text-sm font-bold text-gray-800 mb-2" 
            style={{ 
              fontSize: '13px', 
              marginBottom: '8px',
              fontWeight: '700',
              color: '#1f2937'
            }}
          >
            📋 ข้อมูลออเดอร์
          </div>
          <div className="text-xs text-gray-700 space-y-1.5" style={{ fontSize: '11px' }}>
            <div style={{ marginBottom: '4px' }}>
              <span className="font-semibold">เลขออเดอร์:</span> <span className="ml-1">#{order?.orderNumber || ''}</span>
            </div>
            <div style={{ marginBottom: '4px' }}>
              <span className="font-semibold">วันที่สั่งซื้อ:</span> <span className="ml-1">
                {order?.createdAt ? new Date(order.createdAt.toDate()).toLocaleDateString('th-TH') : ''}
              </span>
            </div>
            {order?.paymentDate && (
              <div style={{ marginBottom: '4px' }}>
                <span className="font-semibold">วันที่ชำระเงิน:</span> <span className="ml-1">
                  {new Date(order.paymentDate.toDate()).toLocaleDateString('th-TH')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div 
          className="p-3 bg-gray-50 rounded border border-gray-200" 
          style={{ 
            padding: '10px',
            backgroundColor: '#f9fafb',
            borderColor: '#e5e7eb',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '0.5rem'
          }}
        >
          <div 
            className="text-sm font-bold text-gray-800 mb-2" 
            style={{ 
              fontSize: '13px', 
              marginBottom: '8px',
              fontWeight: '700',
              color: '#1f2937'
            }}
          >
            📦 ข้อมูลการจัดส่ง
          </div>
          <div className="text-xs text-gray-700 space-y-1.5" style={{ fontSize: '11px' }}>
            {order?.trackingNumber && (
              <div style={{ marginBottom: '4px' }}>
                <span className="font-semibold">เลขพัสดุ:</span> <span className="ml-1">{order.trackingNumber}</span>
              </div>
            )}
            {courierCompany && (
              <div style={{ marginBottom: '4px' }}>
                <span className="font-semibold">บริษัทขนส่ง:</span> <span className="ml-1">{courierCompany}</span>
              </div>
            )}
            {order?.shippingDate && (
              <div style={{ marginBottom: '4px' }}>
                <span className="font-semibold">วันที่ส่ง:</span> <span className="ml-1">
                  {new Date(order.shippingDate.toDate()).toLocaleDateString('th-TH')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recipient Info */}
      <div 
        className="mb-5 p-4 bg-gray-50 rounded-lg border-2 border-gray-300" 
        style={{ 
          marginBottom: '16px', 
          padding: '12px',
          backgroundColor: '#f9fafb',
          borderColor: '#d1d5db',
          borderWidth: '2px',
          borderStyle: 'solid',
          borderRadius: '0.5rem'
        }}
      >
        <div 
          className="text-sm font-bold text-gray-800 mb-3" 
          style={{ 
            fontSize: '14px', 
            marginBottom: '10px',
            fontWeight: '700',
            color: '#1f2937'
          }}
        >
          📦 ผู้รับสินค้า
        </div>
        <div className="grid grid-cols-2 gap-4 mb-3" style={{ marginBottom: '10px' }}>
          <div>
            <div className="text-xs text-gray-600 mb-1" style={{ fontSize: '11px', marginBottom: '4px' }}>ชื่อ-สกุล:</div>
            <div className="text-sm font-semibold text-gray-800" style={{ fontSize: '13px' }}>{displayData.buyerName || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1" style={{ fontSize: '11px', marginBottom: '4px' }}>เบอร์โทร:</div>
            <div className="text-sm font-semibold text-gray-800" style={{ fontSize: '13px' }}>{displayData.buyerPhone || '-'}</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600 mb-1" style={{ fontSize: '11px', marginBottom: '4px' }}>ที่อยู่จัดส่ง:</div>
          <div className="text-sm text-gray-800 leading-relaxed" style={{ fontSize: '12px', lineHeight: '1.6' }}>
            {displayData.shippingAddress?.split('\n').map((line, i) => (
              <div key={i} style={{ marginBottom: '2px' }}>{line}</div>
            )) || '-'}
          </div>
        </div>
      </div>

      {/* Items List */}
      {order?.items && order.items.length > 0 && (
        <div className="mb-5" style={{ marginBottom: '16px' }}>
          <div className="text-sm font-bold text-gray-800 mb-2" style={{ fontSize: '14px', marginBottom: '8px' }}>
            📋 รายการสินค้า
          </div>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <table className="w-full text-sm" style={{ fontSize: '12px' }}>
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700" style={{ padding: '8px 12px', fontSize: '11px' }}>
                    สินค้า
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700" style={{ padding: '8px 12px', fontSize: '11px' }}>
                    จำนวน
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700" style={{ padding: '8px 12px', fontSize: '11px' }}>
                    ราคา
                  </th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="px-3 py-2 text-xs text-gray-800" style={{ padding: '8px 12px', fontSize: '11px' }}>
                      {item.productName}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-gray-600" style={{ padding: '8px 12px', fontSize: '11px' }}>
                      {item.quantity} {item.unit || 'ชิ้น'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-600" style={{ padding: '8px 12px', fontSize: '11px' }}>
                      ฿{item.subtotal?.toLocaleString() || '0'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-3 py-2 text-right text-xs font-semibold text-gray-700" style={{ padding: '8px 12px', fontSize: '11px' }}>
                    ยอดรวม:
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-bold text-gray-800" style={{ padding: '8px 12px', fontSize: '13px' }}>
                    ฿{order.total?.toLocaleString() || '0'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* QR Codes Section */}
      <div className="mb-5 grid grid-cols-2 gap-4" style={{ marginBottom: '16px' }}>
        {qrCodeUrl && (
          <div 
            className="text-center p-3 bg-gray-50 rounded-lg border border-gray-300" 
            style={{ 
              padding: '10px',
              backgroundColor: '#f9fafb',
              borderColor: '#d1d5db',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '0.5rem'
            }}
          >
            <div 
              className="text-xs font-semibold text-gray-700 mb-2" 
              style={{ 
                fontSize: '11px', 
                marginBottom: '6px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              📱 QR Code เบอร์โทรผู้รับ
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
              <img 
                src={qrCodeUrl} 
                alt="Phone QR Code"
                style={{ 
                  width: '110px', 
                  height: '110px',
                  border: '2px solid #000',
                  display: 'block'
                }}
              />
            </div>
            <div className="text-xs text-gray-600" style={{ fontSize: '10px' }}>{displayData.buyerPhone}</div>
          </div>
        )}
        {trackingQrCodeUrl && (
          <div 
            className="text-center p-3 bg-gray-50 rounded-lg border border-gray-300" 
            style={{ 
              padding: '10px',
              backgroundColor: '#f9fafb',
              borderColor: '#d1d5db',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '0.5rem'
            }}
          >
            <div 
              className="text-xs font-semibold text-gray-700 mb-2" 
              style={{ 
                fontSize: '11px', 
                marginBottom: '6px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              📦 QR Code ติดตามพัสดุ
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
              <img 
                src={trackingQrCodeUrl} 
                alt="Tracking QR Code"
                style={{ 
                  width: '110px', 
                  height: '110px',
                  border: '2px solid #000',
                  display: 'block'
                }}
              />
            </div>
            <div className="text-xs text-gray-600" style={{ fontSize: '10px' }}>{order.trackingNumber}</div>
          </div>
        )}
      </div>

      {/* Notes */}
      {displayData.notes && (
        <div 
          className="mb-5 p-3 bg-yellow-50 rounded-lg border border-yellow-300" 
          style={{ 
            marginBottom: '16px', 
            padding: '10px',
            backgroundColor: '#fefce8',
            borderColor: '#fde047',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '0.5rem'
          }}
        >
          <div 
            className="text-xs font-semibold text-gray-700 mb-1" 
            style={{ 
              fontSize: '11px', 
              marginBottom: '4px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            📝 หมายเหตุ:
          </div>
          <div className="text-xs text-gray-700" style={{ fontSize: '11px', lineHeight: '1.4' }}>{displayData.notes}</div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-5 pt-3 border-t border-gray-300 text-center text-xs text-gray-500" style={{ marginTop: '16px', paddingTop: '10px' }}>
        <div style={{ fontSize: '10px' }}>พิมพ์เมื่อ: {new Date().toLocaleString('th-TH')}</div>
        {store?.contact?.lineId && (
          <div className="mt-1" style={{ marginTop: '4px', fontSize: '10px' }}>LINE: {store.contact.lineId}</div>
        )}
      </div>
    </div>
  );
};

export default ShippingLabelFull;

